import { onCookieGo } from '@/app/common/ccm/cookieGo';

const POLLING_INTERVAL_MS = 500;

// One timer and one `cookieGO` listener for every hook that waits on the CCM. A page mounts
// several of them - the consent hooks on both page trees, the search popups, the modal restore -
// and each one used to run a timer of its own. The timer starts with the first watcher and stops
// with the last, so a page where nothing is waiting any more runs no timer at all.
const watchers = new Set<() => void>();
let interval: ReturnType<typeof setInterval> | undefined;
let unsubscribeCookieGo: (() => void) | undefined;

const runWatchers = () => {
  // Copy first: a watcher that has seen what it waited for removes itself while this runs.
  [...watchers].forEach((watcher) => watcher());
};

/**
 * Calls `watcher` on the `cookieGO` event and on a shared poll. Returns a function that stops it.
 *
 * The poll is not a duplicate of the event and must stay: CCM fires `cookieGO` only from
 * AcceptCookiesUseCase (`oil_optin_done`), so a returning visitor whose cookie is rewritten by
 * SetCookieAlreadyAcceptedUseCase (`oil_has_optedin`) gets no event at all. The `platformData`
 * check for an event that already fired can also lag, because CCM holds its platformData pushes
 * back until the page has a `visit` entry. And a grace period runs out with no event to announce it.
 */
export const watchForConsent = (watcher: () => void): (() => void) => {
  watchers.add(watcher);

  if (watchers.size === 1) {
    // The timer first: onCookieGo runs the watchers straight away when the event has already
    // fired, and a watcher that stops itself in that call must find a timer it can clear.
    interval = setInterval(runWatchers, POLLING_INTERVAL_MS);
    unsubscribeCookieGo = onCookieGo(runWatchers);
  }

  return () => {
    watchers.delete(watcher);
    if (watchers.size > 0) return;

    unsubscribeCookieGo?.();
    unsubscribeCookieGo = undefined;
    clearInterval(interval);
    interval = undefined;
  };
};
